<template>
  <div class="min-h-screen bg-gray-900 text-white py-3 px-2 sm:py-6 sm:px-4 lg:px-8">
    <div class="max-w-7xl mx-auto">
      <DataListControls v-model:searchText="searchText" v-model:pageSize="pageSize" v-model:currentPage="currentPage"
        collectionName="services" @dataUpdate="handleDataUpdate">
        <template #actions>
          <button @click="openAddForm"
            class="inline-flex items-center px-3 py-2.5 sm:px-4 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 min-h-[44px] touch-manipulation">
            <span class="hidden sm:inline">Add Service</span>
            <span class="sm:hidden">Add</span>
          </button>
        </template>
        <template #table>
          <div class="overflow-x-auto -mx-2 sm:mx-0">
            <table class="min-w-full divide-y divide-gray-700">
              <thead class="bg-gray-800">
                <tr>
                  <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Action</th>
                  <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">Title</th>
                  <th class="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider hidden sm:table-cell">Status</th>
                </tr>
              </thead>
              <tbody class="bg-gray-700 divide-y divide-gray-600">
                <tr v-for="service in services" :key="service.id" class="hover:bg-gray-600">
                  <td class="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium relative">
                    <div class="relative inline-block text-left dropdown-container">
                      <button @click="toggleDropdown(service.id)"
                        class="inline-flex items-center justify-center w-10 h-10 sm:w-8 sm:h-8 bg-gray-600 bg-opacity-50 hover:bg-opacity-75 rounded-full transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 touch-manipulation">
                        <svg class="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                        </svg>
                      </button>

                      <div v-if="openDropdownId === service.id"
                        class="absolute left-0 top-0 mt-10 w-40 bg-gray-800 bg-opacity-95 backdrop-blur-sm border border-gray-600 rounded-lg shadow-lg z-50">
                        <div class="py-1">
                          <button @click="openEditForm(service); closeDropdown()"
                            class="w-full text-left px-4 py-2 text-sm text-blue-400 hover:bg-blue-600 hover:bg-opacity-20 hover:text-blue-300 transition-all duration-200 flex items-center">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          <button @click="openDeleteDialog(service); closeDropdown()"
                            class="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-red-600 hover:bg-opacity-20 hover:text-red-300 transition-all duration-200 flex items-center">
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td class="px-2 sm:px-6 py-3 sm:py-4 text-white">
                    <div class="flex items-center">
                      <div class="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 mr-2 sm:mr-3">
                        <img :src="service.imageURLs || 'https://via.placeholder.com/40'"
                          class="h-full w-full rounded object-cover" alt="Service image" />
                      </div>
                      <span class="text-sm sm:text-base">{{ service.title || 'N/A' }}</span>
                    </div>
                  </td>

                  <td class="px-2 sm:px-6 py-3 sm:py-4 whitespace-nowrap hidden sm:table-cell">
                    <label class="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" :checked="service.status" @change="toggleStatus(service)"
                        class="sr-only peer" :disabled="updatingStatus === service.id">
                      <div
                        class="w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-800 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600">
                      </div>
                      <span class="ml-3 text-sm font-medium"
                        :class="service.status ? 'text-green-400' : 'text-gray-400'">
                        {{ service.status ? 'Active' : 'Inactive' }}
                      </span>
                    </label>
                  </td>

                  <!-- <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button @click="openEditForm(service)" class="text-blue-400 hover:text-blue-300 mr-4">Edit</button>
                    <button @click="openDeleteDialog(service)" class="text-red-400 hover:text-red-300">Delete</button>
                  </td> -->
                </tr>
                <tr v-if="!services.length && !isLoading" class="bg-gray-700">
                  <td colspan="2" class="px-2 sm:px-6 py-4 text-center text-gray-400 sm:hidden">
                    No Data found
                  </td>
                  <td colspan="3" class="px-6 py-4 text-center text-gray-400 hidden sm:table-cell">
                    No Data found
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </template>
      </DataListControls>

      <ServiceForm :isVisible="showForm" :serviceId="selectedServiceId" @onEmitHandleEvent="handleFormEvent" />
      <DeleteConfirmation :isVisible="showDeleteDialog" :deleteId="selectedServiceId"
        :elementName="selectedServiceTitle" elementCategory="Service" collectionName="services"
        :isLastRecordOnPage="isLastRecordOnPage" @onEmitHandleEvent="handleDeleteEvent" />
      
      <!-- Toast Notification -->
      <ToastedVue v-if="showToast" :informMessage="toastMessage" :isFailed="toastFailed"
        @onAutoClose="handleCloseToast" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { doc, updateDoc } from 'firebase/firestore';
import { projectFirestore } from '@/firebase/config';
import DataListControls from '@/components/DataListControls.vue';
import ServiceForm from '@/components/ServiceForm.vue';
import DeleteConfirmation from '@/components/DeleteConfirmation.vue';
import ToastedVue from '@/components/ToastedVue.vue';

const searchText = ref('');
const pageSize = ref(10);
const currentPage = ref(1);
const services = ref([]);
const showForm = ref(false);
const showDeleteDialog = ref(false);
const selectedServiceId = ref(null);
const selectedServiceTitle = ref('');
const isLoading = ref(false);
const openDropdownId = ref(null);
const updatingStatus = ref(null);
const showToast = ref(false);
const toastMessage = ref('');
const toastFailed = ref(false);

const isLastRecordOnPage = computed(() => {
  return services.value.length === 1 && currentPage.value > 1;
});



const handleDataUpdate = (data) => {
  services.value = data;
  isLoading.value = false;
};

const openAddForm = () => {
  selectedServiceId.value = null;
  showForm.value = true;
};

const openEditForm = (service) => {
  selectedServiceId.value = service.id;
  showForm.value = true;
};

const openDeleteDialog = (service) => {
  selectedServiceId.value = service.id;
  selectedServiceTitle.value = service.title || 'Unnamed Service';
  showDeleteDialog.value = true;
};

const toggleDropdown = (serviceId) => {
  openDropdownId.value = openDropdownId.value === serviceId ? null : serviceId;
};

const closeDropdown = () => {
  openDropdownId.value = null;
};

const toggleStatus = async (service) => {
  try {
    updatingStatus.value = service.id;
    const newStatus = !service.status;

    // Update in Firestore
    const serviceRef = doc(projectFirestore, 'services', service.id);
    await updateDoc(serviceRef, {
      status: newStatus
    });

    // Update local state
    const serviceIndex = services.value.findIndex(s => s.id === service.id);
    if (serviceIndex !== -1) {
      services.value[serviceIndex].status = newStatus;
    }

    console.log(`service ${service.title} status updated to: ${newStatus ? 'Active' : 'Inactive'}`);
  } catch (error) {
    console.error('Error updating service status:', error);
    alert(`Failed to update service status: ${error.message}`);
  } finally {
    updatingStatus.value = null;
  }
};

const handleFormEvent = (event, hasError = false, message = '') => {
  if (event === 'close') {
    showForm.value = false;
  } else if (event === 'created' || event === 'updated') {
    showForm.value = false;
    showToast.value = true;
    toastMessage.value = event === 'created' ? 'Service created successfully!' : 'Service updated successfully!';
    toastFailed.value = false;
    if (event === 'created') {
      currentPage.value = 1;
    }
  } else if (event === 'error') {
    console.error('Form error:', message);
    showToast.value = true;
    toastMessage.value = message || 'Failed to save service. Please try again.';
    toastFailed.value = true;
  }
};

const handleCloseToast = () => {
  showToast.value = false;
  toastMessage.value = '';
  toastFailed.value = false;
};

const handleDeleteEvent = (event, hasError = false, message = '') => {
  if (event === 'close') {
    showDeleteDialog.value = false;
  } else if (event === 'deleted') {
    showDeleteDialog.value = false;
    if (isLastRecordOnPage.value) {
      currentPage.value = Math.max(1, currentPage.value - 1);
    }
  } else if (event === 'error') {
    console.error('Delete error:', message);
    alert(`Failed to delete service: ${message}`);
  }
};
</script>

<style scoped>
tr {
  transition: background-color 0.2s ease;
}

button {
  transition: color 0.2s ease, background-color 0.2s ease;
}

.touch-manipulation {
  touch-action: manipulation;
}
</style>